import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChevronLeft, Wand2, CheckCircle2, Download, Images, Send, HardDrive } from "lucide-react";
import { api, mediaUrl, errMsg, triggerDownload } from "@/lib/galleryApi";
import { useGalleryUpload } from "@/hooks/useGalleryUpload";
import { AccessBadge } from "@/components/gallery/accessModes";
import { UploadPanel } from "@/components/gallery/UploadPanel";
import { PhotoGrid } from "@/components/gallery/PhotoGrid";
import { SelectionBar } from "@/components/gallery/SelectionBar";
import { AdminLightbox } from "@/components/gallery/AdminLightbox";
import { SharePanel } from "@/components/gallery/SharePanel";
import { ZipPartsDialog } from "@/components/gallery/ZipPartsDialog";
import { DeliveryDialog } from "@/components/gallery/DeliveryDialog";
import { PurgeDialog, PurgeProgress, usePurgeJob, formatBytes } from "@/components/gallery/PurgeDialog";
import { ClientGalleryView } from "@/components/gallery/ClientGalleryView";
import { useStudioPerms } from "@/hooks/useStudioPerms";
import { RetentionPanel } from "@/components/gallery/RetentionPanel";
import { ArchivePanel } from "@/components/gallery/ArchivePanel";
import { PhaseBadge } from "@/components/gallery/retentionUi";

const live = (p) => !p.in_trash && !p.archived;
const FILTERS = [
  ["all", "Toutes", live],
  ["favorite", "Favoris", (p) => live(p) && p.is_favorite],
  ["client_fav", "Favoris du client", (p) => live(p) && p.client_favorites?.length > 0],
  ["processed", "Traitees", (p) => live(p) && p.has_processed],
  ["published", "Publiees", (p) => live(p) && p.status === "published"],
  ["unpublished", "Non publiees", (p) => live(p) && p.status !== "published" && !p.is_rejected],
  ["rejected", "Rejetees", (p) => live(p) && p.is_rejected],
  ["archived", "Archivees", (p) => !p.in_trash && p.archived],
  ["trash", "Corbeille", (p) => p.in_trash],
];
const LOCAL = {
  favorite: { is_favorite: true }, unfavorite: { is_favorite: false }, reject: { is_rejected: true }, unreject: { is_rejected: false },
  archive: { archived: true }, unarchive: { archived: false }, trash: { in_trash: true }, restore: { in_trash: false, archived: false },
};
const DONE_MSG = { archive: "archivee(s)", unarchive: "restauree(s)", trash: "retiree(s) — recuperables dans la Corbeille", restore: "restauree(s)" };

export default function GalleryDetailPage() {
  const { galleryId } = useParams();
  const perms = useStudioPerms();
  if (!perms) return <div className="min-h-screen bg-[#060B14]" />;
  if (perms.studio) return <StudioGalleryDetail galleryId={galleryId} />;
  if (perms.client) return <ClientGalleryView galleryId={galleryId} />;
  return <div className="min-h-screen bg-[#060B14] flex items-center justify-center text-sm text-[#94A3B8]">Acces non autorise</div>;
}

function StudioGalleryDetail({ galleryId }) {
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [lightbox, setLightbox] = useState(null);
  const [zipParts, setZipParts] = useState(null);
  const [purgeIds, setPurgeIds] = useState(null);
  const [delivery, setDelivery] = useState(false);
  const [storage, setStorage] = useState(null);

  const loadStorage = useCallback(() => api.get(`/galleries/${galleryId}/storage`).then(setStorage).catch(() => {}), [galleryId]);
  const reloadPhotos = useCallback(() => { api.get(`/galleries/${galleryId}/photos`).then(setPhotos).catch(() => {}); loadStorage(); }, [galleryId, loadStorage]);
  useEffect(() => {
    Promise.all([api.get(`/galleries/${galleryId}`), api.get(`/galleries/${galleryId}/photos`)])
      .then(([g, p]) => { setGallery(g); setPhotos(p); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
    loadStorage();
  }, [galleryId, loadStorage]);

  const purge = usePurgeJob(() => { loadStorage(); toast.success("Suppression definitive terminee"); });
  const onPhoto = useCallback((p) => setPhotos((prev) => [...prev, p]), []);
  const upload = useGalleryUpload(galleryId, onPhoto);
  useEffect(() => {
    if (!upload.running && upload.total > 0) { loadStorage(); toast.success(`Import termine : ${upload.done - upload.failed.filter((f) => f.file).length - upload.skipped} nouvelle(s) photo(s)`); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upload.running]);

  const visible = useMemo(() => photos.filter(FILTERS.find((f) => f[0] === filter)[2]), [photos, filter]);
  const allSelected = visible.length > 0 && visible.every((p) => selected.has(p.id));
  const counts = useMemo(() => Object.fromEntries(FILTERS.map(([k, , fn]) => [k, photos.filter(fn).length])), [photos]);
  const patchLocal = (ids, upd) => setPhotos((prev) => prev.map((p) => (ids.includes(p.id) ? { ...p, ...upd } : p)));

  const bulk = async (action, ids = [...selected]) => {
    try {
      if (action === "publish") {
        const r = await api.post(`/galleries/${galleryId}/publish`, { photo_ids: ids });
        patchLocal(ids.filter((id) => !photos.find((p) => p.id === id)?.is_rejected), { status: "published" });
        toast.success(`${r.published_count} photo(s) publiee(s)`);
        return;
      }
      await api.post(`/galleries/${galleryId}/photos/bulk`, { action, photo_ids: ids });
      if (action === "unpublish") setPhotos((prev) => prev.map((p) => (ids.includes(p.id) ? { ...p, status: p.has_processed ? "processed" : "imported" } : p)));
      else patchLocal(ids, LOCAL[action]);
      if (DONE_MSG[action]) { setSelected(new Set()); toast.success(`${ids.length} photo(s) ${DONE_MSG[action]}`); }
      else if (ids.length > 1) toast.success("Selection mise a jour");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const publishAll = async () => {
    try {
      const r = await api.post(`/galleries/${galleryId}/publish`, {});
      setPhotos((prev) => prev.map((p) => (p.is_rejected || !live(p) ? p : { ...p, status: "published" })));
      toast.success(`${r.published_count} photo(s) publiee(s)${gallery.access_mode === "private" ? " — pensez a choisir un mode de partage" : ""}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const zip = async (ids) => {
    try {
      const r = await api.post(`/galleries/${galleryId}/zip-link`, { photo_ids: ids });
      if (r.parts.length > 1) return setZipParts(r);
      triggerDownload(mediaUrl(r.url));
      toast.success(`Telechargement du ZIP (${r.count} photos)…`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setCover = async (p) => {
    await api.patch(`/galleries/${galleryId}`, { cover_photo_id: p.id });
    setGallery((g) => ({ ...g, cover_photo_id: p.id }));
    toast.success("Photo de couverture definie");
  };

  const onPurgeStarted = (job, ids) => {
    setPhotos((prev) => prev.filter((p) => !ids.includes(p.id)));
    setSelected(new Set());
    purge.track(job);
  };

  const openStudio = (ids) => navigate(`/studio/${galleryId}${ids?.length ? `?ids=${ids.join(",")}` : ""}`);

  if (loading) return <div className="min-h-screen bg-[#060B14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0099CC]/30 border-t-[#0099CC] rounded-full animate-spin" /></div>;
  if (!gallery) return <div className="min-h-screen bg-[#060B14] flex items-center justify-center text-white">Galerie introuvable</div>;
  const liveCount = counts.all;

  return (
    <div className="min-h-screen bg-[#060B14] text-white" data-testid="gallery-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        <button onClick={() => navigate("/app/galeries")} className="text-xs text-[#64748B] hover:text-white flex items-center gap-1 mb-4" data-testid="back-to-galleries"><ChevronLeft className="w-4 h-4" />Galeries</button>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap"><h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate" data-testid="gallery-title">{gallery.title}</h1><AccessBadge mode={gallery.access_mode} testId="gallery-access-badge" /><PhaseBadge r={gallery.retention} testId="gallery-phase-badge" /></div>
            <div className="flex items-center gap-4 mt-2 text-xs text-[#64748B] flex-wrap">
              {gallery.event_title && <span>{gallery.event_title}</span>}
              <span className="flex items-center gap-1" data-testid="gallery-photo-count"><Images className="w-3.5 h-3.5" />{counts.all} photos</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />{counts.published} publiees</span>
              {storage && <span className="flex items-center gap-1" data-testid="gallery-storage" title={`Originaux ${formatBytes(storage.bytes_originals)} · retouches ${formatBytes(storage.bytes_processed)} · apercus ${formatBytes(storage.bytes_variants)}`}><HardDrive className="w-3.5 h-3.5" />{formatBytes(storage.bytes_total)}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => zip(null)} disabled={!liveCount} className="bg-transparent border-[#1A2332] text-[#94A3B8]" data-testid="download-all-zip"><Download className="w-4 h-4 mr-2" />Tout telecharger</Button>
            <Button onClick={() => openStudio()} disabled={!liveCount} className="bg-[#F97316] hover:bg-[#EA580C] text-white" data-testid="open-studio-from-gallery"><Wand2 className="w-4 h-4 mr-2" />Ouvrir le Studio Photo</Button>
            <Button onClick={publishAll} disabled={!liveCount} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="publish-gallery-btn"><CheckCircle2 className="w-4 h-4 mr-2" />Publier la galerie</Button>
            <Button onClick={() => setDelivery(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="send-to-client-btn"><Send className="w-4 h-4 mr-2" />Envoyer la galerie au client</Button>
          </div>
        </div>

        <Tabs defaultValue="photos">
          <TabsList className="bg-[#0B1120] border border-[#1A2332] mb-6 max-w-full overflow-x-auto justify-start">
            <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>
            <TabsTrigger value="share" data-testid="tab-share">Partage & confidentialite</TabsTrigger>
            <TabsTrigger value="retention" data-testid="tab-retention">Disponibilite & selection</TabsTrigger>
            <TabsTrigger value="archives" data-testid="tab-archives">Sauvegardes et restaurations</TabsTrigger>
          </TabsList>
          <TabsContent value="photos">
            <UploadPanel upload={upload} />
            <PurgeProgress job={purge.job} onClose={purge.clear} />
            <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1" data-testid="photo-filters">
              {FILTERS.map(([k, label]) => (
                <button key={k} onClick={() => { setFilter(k); setSelected(new Set()); }} data-testid={`filter-${k}`}
                  className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-white text-black border-white" : "border-[#1A2332] text-[#94A3B8] hover:text-white"}`}>
                  {label} <span className="opacity-60">{counts[k]}</span>
                </button>
              ))}
              <button onClick={() => setSelected(allSelected ? new Set() : new Set(visible.map((p) => p.id)))} className="ml-auto shrink-0 text-xs text-[#0099CC] hover:underline" data-testid="select-all-btn">
                {allSelected ? "Tout deselectionner" : "Tout selectionner"}
              </button>
            </div>
            {filter === "trash" && <p className="text-xs text-[#64748B] mb-3">Photos retirees de la galerie : invisibles pour les clients, fichiers conserves. Restaurez-les ou supprimez-les definitivement.</p>}
            {filter === "archived" && <p className="text-xs text-[#64748B] mb-3">Photos archivees : invisibles pour les clients et exclues des ZIP ; original et retouches conserves.</p>}
            <SelectionBar count={selected.size} view={filter} onAction={(a) => bulk(a)} onClear={() => setSelected(new Set())}
              onStudio={() => openStudio([...selected])} onZip={() => zip([...selected])} onPurge={() => setPurgeIds([...selected])} />
            {visible.length === 0
              ? <p className="text-center text-sm text-[#64748B] py-16" data-testid="photos-empty">{photos.length ? "Aucune photo dans ce filtre" : "Aucune photo — commencez par importer"}</p>
              : <PhotoGrid photos={visible} selected={selected} setSelected={setSelected} coverId={gallery.cover_photo_id} onOpen={setLightbox} />}
          </TabsContent>
          <TabsContent value="share"><SharePanel gallery={gallery} onSaved={(g) => setGallery((old) => ({ ...old, ...g }))} /></TabsContent>
          <TabsContent value="retention"><RetentionPanel galleryId={galleryId} storage={storage} /></TabsContent>
          <TabsContent value="archives"><ArchivePanel gallery={gallery} photos={photos} onPhotosChanged={reloadPhotos} onSend={() => setDelivery(true)} /></TabsContent>
        </Tabs>
      </div>
      <ZipPartsDialog zip={zipParts} onClose={() => setZipParts(null)} />
      <PurgeDialog ids={purgeIds} onClose={() => setPurgeIds(null)} galleryId={galleryId} onStarted={onPurgeStarted} />
      <DeliveryDialog open={delivery} onOpenChange={setDelivery} gallery={gallery} />
      {lightbox !== null && (
        <AdminLightbox photos={visible} index={lightbox} setIndex={setLightbox} coverId={gallery.cover_photo_id}
          onFavorite={(p) => bulk(p.is_favorite ? "unfavorite" : "favorite", [p.id])}
          onReject={(p) => bulk(p.is_rejected ? "unreject" : "reject", [p.id])} onCover={setCover} />
      )}
    </div>
  );
}
